import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PublicFilterDropdown from '../components/PublicFilterDropdown';
import { CheckboxFilter } from '../components/FilterPanel';
import { DirectoryHeader, DirectoryPage, LastUpdated, PillTabs, UnderlineTabs } from '../components/Directory';
import AnimalHealthFacilities from '../components/Facilities/AnimalHealthFacilities';
import ProcessingFacilities from '../components/Facilities/ProcessingFacilities';
import CentresTable from '../components/Facilities/CentresTable';
import FacilityMapView from '../components/Facilities/FacilityMapView';
import OperationalStatusBadge from '../components/Facilities/OperationalStatusBadge';
import ProfessionalStatusBadge from '../components/ProfessionalStatusBadge';
import { ANIMAL_HEALTH_CATEGORIES } from '../data/animalHealthData';
import {
  BREEDING_CATEGORIES,
  COLD_CHAIN_FACILITIES,
  FEED_NUTRITION_FACILITIES,
  NIGERIA_FACILITY_STATES,
  PROCESSING_CATEGORIES,
  FACILITIES_LAST_UPDATED,
} from '../data/facilitiesData';

// Figma 497:47867, 498:48375, 499:49440, 1157:29645, 1299:70489 — five
// top-level tabs, each with "All States" + "Facility Type" filters
const TOP_TABS = [
  { key: 'animal-health', label: 'Animal Health Facilities' },
  { key: 'processing',    label: 'Processing & Value-Addition' },
  { key: 'feed',          label: 'Feed & Nutrition' },
  { key: 'cold-chain',    label: 'Cold Chain & Logistics' },
  { key: 'breeding',      label: 'Breeding & Genetic Improvement' },
];

const processingRows = (key) => PROCESSING_CATEGORIES.find((c) => c.key === key)?.rows || [];

// Feed & Nutrition and Cold Chain & Logistics are single tables
const TABLE_ROWS = { feed: FEED_NUTRITION_FACILITIES, 'cold-chain': COLD_CHAIN_FACILITIES };

// Breeding & Genetic Improvement sub-tab pills
const BREEDING_TABS = BREEDING_CATEGORIES;

// Older links (Overview cards, bookmarks) used the previous tab keys
const LEGACY_TABS = { veterinary: 'animal-health', stations: 'animal-health', ai: 'breeding' };

function initialTab(requested) {
  const key = LEGACY_TABS[requested] || requested;
  return TOP_TABS.some((tab) => tab.key === key) ? key : TOP_TABS[0].key;
}

const matchesTypes = (types, type) => !types.length || types.includes(type);

export default function FacilitiesServices() {
  const location = useLocation();

  const [topTab, setTopTab] = useState(() => initialTab(location.state?.topTab));
  const [state,  setState]  = useState(NIGERIA_FACILITY_STATES[0]);
  const [query,  setQuery]  = useState('');

  // Sub-tab state lifted here so the Facility Type options follow it
  const [healthCategory,     setHealthCategory]     = useState(ANIMAL_HEALTH_CATEGORIES[0].key);
  const [processingCategory, setProcessingCategory] = useState(location.state?.category || PROCESSING_CATEGORIES[0].key);
  const [breedingTab,        setBreedingTab]        = useState(location.state?.topTab === 'ai' ? 'ai' : BREEDING_TABS[0].key);
  // Multi-select "Facility Type" panel (Figma 1301:75466) — [] means all types
  const [facilityTypes,      setFacilityTypes]      = useState([]);
  // Record whose "View" was clicked (Animal Health / Processing tables)
  const [mapRecord,          setMapRecord]          = useState(null);

  // Unfiltered rows behind whichever tab / sub-tab is active
  const tabRows = useMemo(() => {
    if (topTab === 'animal-health') return ANIMAL_HEALTH_CATEGORIES.find((c) => c.key === healthCategory).rows;
    if (topTab === 'processing') return processingRows(processingCategory);
    if (topTab === 'breeding') return BREEDING_TABS.find((t) => t.key === breedingTab).rows;
    return TABLE_ROWS[topTab];
  }, [topTab, healthCategory, processingCategory, breedingTab]);

  const typeOptions = useMemo(() => [...new Set(tabRows.map((r) => r.type))], [tabRows]);

  const filteredRows = useMemo(() => tabRows.filter((r) =>
    (state === 'All States' || r.state === state) && matchesTypes(facilityTypes, r.type)
  ), [tabRows, state, facilityTypes]);

  // Every tab / sub-tab change starts the Facility Type filter afresh
  const withTypeReset = (setter) => (key) => {
    setter(key);
    setFacilityTypes([]);
  };

  // Figma's tab row carries the two pills alone, without a "Filter By:" label
  const filters = (
    <div className="flex items-center gap-6">
      <PublicFilterDropdown
        value={state}
        options={NIGERIA_FACILITY_STATES}
        onChange={setState}
        ariaLabel="Filter by state"
        align="right"
      />
      <CheckboxFilter
        label="Facility Type"
        options={typeOptions}
        selected={facilityTypes}
        onChange={setFacilityTypes}
        align="right"
      />
    </div>
  );

  // Figma 1293:53438 — the map view replaces the heading, search and tabs;
  // its chip names the sub-tab the record was opened from
  if (mapRecord) {
    const isHealth = topTab === 'animal-health';
    const category = isHealth
      ? ANIMAL_HEALTH_CATEGORIES.find((c) => c.key === healthCategory)
      : PROCESSING_CATEGORIES.find((c) => c.key === processingCategory);
    const Badge = isHealth ? ProfessionalStatusBadge : OperationalStatusBadge;
    return (
      <DirectoryPage>
        <FacilityMapView
          record={mapRecord}
          onBack={() => setMapRecord(null)}
          badge={<Badge status={mapRecord.status} />}
          label={category?.label}
          lastUpdated={FACILITIES_LAST_UPDATED}
        />
      </DirectoryPage>
    );
  }

  return (
    <DirectoryPage>
      <DirectoryHeader
        eyebrow="Livestock Facilities & Services Directory"
        subject="Livestock Facilities & Services"
        query={query}
        onQueryChange={setQuery}
      />

      {/* Tabs, sub-tabs and content keep Livestock Market Prices' 16px rhythm */}
      <div className="flex flex-col gap-4">
        <UnderlineTabs tabs={TOP_TABS} active={topTab} onChange={withTypeReset(setTopTab)} right={filters} />

        {topTab === 'animal-health' && (
          <AnimalHealthFacilities
            rows={filteredRows}
            searchQuery={query}
            activeCategory={healthCategory}
            onCategoryChange={withTypeReset(setHealthCategory)}
            onView={setMapRecord}
          />
        )}

        {topTab === 'processing' && (
          <ProcessingFacilities
            state={state}
            searchQuery={query}
            activeCategory={processingCategory}
            facilityTypes={facilityTypes}
            onCategoryChange={withTypeReset(setProcessingCategory)}
            onView={setMapRecord}
          />
        )}

        {topTab in TABLE_ROWS && <CentresTable tableKey={topTab} rows={filteredRows} searchQuery={query} />}

        {topTab === 'breeding' && (
          <div className="flex flex-col gap-4">
            <PillTabs tabs={BREEDING_TABS} active={breedingTab} onChange={withTypeReset(setBreedingTab)} singleLine />
            <CentresTable tableKey={breedingTab} rows={filteredRows} searchQuery={query} typeTitle="Category" />
          </div>
        )}
      </div>

      <LastUpdated date={FACILITIES_LAST_UPDATED} />
    </DirectoryPage>
  );
}
